import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Calendar, Tag, UserPen } from "lucide-react";
import { getDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { format, parseISO } from "date-fns";
import Header from "../components/Header";
import StickBar from "../components/StickBar";
import VideoPlayer from "../components/VideoPlayer";
import {
  markdownComponents,
  parseMarkdownText,
} from "../utils/markdownComponents";
import { CircularProgress } from "@mui/material";
import ReactMarkdown from "react-markdown";
import { SERVER_URL } from "../globalVariables";

const BlogDetail = () => {
  const { blogId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const passedBlog = location.state?.blog || null;
  const [blog, setBlog] = useState(passedBlog);
  const [isLoading, setIsLoading] = useState(passedBlog ? false : true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [blogId]);

  useEffect(() => {
    const getBlog = async () => {
      fetch(`${SERVER_URL}/api/getBlog/${blogId}`)
        .then((response) => response.json())
        .then((data) => {
          if (data) {
            setBlog(data);
          } else {
            navigate("/");
          }
        })
        .catch((error) => {
          navigate("/");
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    if (blogId && !blog) {
      getBlog();
    }
  }, [blog, blogId, navigate]);

  const renderSection = (section, index) => {
    const content = parseMarkdownText(section?.content);

    switch (section.type) {
      case "text":
        return (
          <div key={index} className="mb-10">
            {section.title && (
              <h2 className="text-2xl font-bold text-white mb-6">
                {section.title}
              </h2>
            )}
            <div className="prose prose-lg prose-indigo text-white">
              <ReactMarkdown components={markdownComponents} breaks={true}>
                {content}
              </ReactMarkdown>
            </div>
          </div>
        );

      case "image":
        return (
          <div
            key={index}
            className={`mb-10 flex flex-col md:flex-row ${
              section.position === "right"
                ? "md:flex-row"
                : "md:flex-row-reverse"
            } gap-8`}
          >
            <div className="flex-1 order-1 md:order-none">
              {section.title && (
                <h2 className="text-2xl font-bold text-white mb-6">
                  {section.title}
                </h2>
              )}
              <div className="prose prose-lg prose-indigo text-white">
                <ReactMarkdown components={markdownComponents} breaks={true}>
                  {content}
                </ReactMarkdown>
              </div>
            </div>

            <div className="flex-1 order-2 md:order-none">
              <img
                src={section.src}
                alt={section.title || "Blog image"}
                className="shadow-lg w-full h-auto"
              />
            </div>
          </div>
        );

      case "video":
        const getVideoPlayer = (src, title = "Blog video") => {
          if (!src) return null;

          if (src.includes("youtu")) {
            const videoId = src.includes("youtu.be")
              ? src.split("/").pop()?.split("?")[0]
              : new URL(src).searchParams.get("v");

            return (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                title={title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-[500px]"
              />
            );
          }

          return <VideoPlayer path={src} autoPlay={true} />;
        };

        return (
          <div key={index} className="mb-10">
            {section.title && (
              <h2 className="text-2xl font-bold text-white mb-6">
                {section.title}
              </h2>
            )}

            {section.content && (
              <div className="prose prose-lg prose-indigo text-white mb-6">
                <ReactMarkdown components={markdownComponents} breaks={true}>
                  {content}
                </ReactMarkdown>
              </div>
            )}

            <div className="aspect-w-16 aspect-h-9">
              {getVideoPlayer(section.src, section.title)}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex-1 lg:max-w-5xl lg:mx-auto">
      <Header />

      <div className="flex w-full">
        <StickBar />

        {isLoading ? (
          <div className="flex w-full justify-center items-center h-[90vh]">
            <CircularProgress
              sx={{ color: "#33c7b6" }}
              style={{ height: 60, width: 60 }}
            />
          </div>
        ) : (
          <div className="w-full lg:pl-[110px] p-6 md:p-8">
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {blog?.title}
              </h1>

              <div className="flex flex-wrap items-center text-white mb-6 gap-4">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>
                    {blog?.createdAt &&
                      format(parseISO(blog?.createdAt), "dd/MM/yyyy")}
                  </span>
                </div>

                <div className="flex items-center">
                  <Tag className="h-4 w-4 mr-1" />
                  <span>{blog?.category}</span>
                </div>

                <div className="flex items-center">
                  <UserPen className="h-4 w-4 mr-1" />
                  <span>{blog?.author}</span>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <img
                src={blog?.coverImage}
                alt={blog?.id}
                className="w-full h-auto shadow-md object-cover"
                style={{ maxHeight: "500px" }}
              />
            </div>

            <div className="mb-8">
              {blog?.sections.map((section, index) =>
                renderSection(section, index)
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogDetail;
